import type { Job } from "@malfak/contracts";
import { SendErrorNotice } from "@/features/settings/reauth";
import { useT } from "@/lib/i18n";
import { isReauth, TEMPORAL_WRAPPER } from "@/lib/reauth";
import { isActive, isSendKind } from "./model";

/**
 * Why a finished job failed, through the shared error-code mapping (ALP-77): a rejected channel token gets the
 * "Instagram needs to be reconnected" notice with Reconnect; other codes their localized line (the raw error only as
 * secondary detail, never Temporal's "Activity task failed" wrapper). Retry stays the job's own Retry control.
 * Jobs without a code that are not sends keep their raw error text (resync, import, export…). Legacy rows (QA F16:
 * Temporal's wrapper, code `unknown`) read as a localized generic failure; the job's Retry control stays.
 */
export function JobError({ job }: { job: Pick<Job, "status" | "error" | "errorCode" | "target" | "kind"> }) {
  const { t } = useT();
  if (isActive(job.status)) return null;
  const mapped = job.status === "failed" && (!!job.errorCode || isSendKind(job.kind) || !!job.error?.startsWith(TEMPORAL_WRAPPER));
  if (mapped) {
    const user = job.target?.type === "account" ? (job.target.label ?? "").replace(/^[\s‎@]+/, "") : "";
    return <SendErrorNotice failure={job} username={user || null} className="mt-2" testId={isReauth(job) ? "job-reauth" : "job-error"} genericMessage={isSendKind(job.kind) ? undefined : t("jobs.drawer.failedGeneric")} />;
  }
  return job.error ? <p className="mt-2 text-[12px] text-bad">{job.error}</p> : null;
}
